/* This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at http://mozilla.org/MPL/2.0/. */

import { SourceMapConsumer as LibSourceMapConsumer } from 'source-map';
import type { RawSourceMap } from 'source-map';
import type { IndexIntoSourceTable } from 'firefox-profiler/types';

export type SourceMapConsumer = LibSourceMapConsumer;

/**
 * Holds pre-parsed SourceMapConsumer instances keyed by IndexIntoSourceTable.
 * Initialized from the resolvedSourceMaps already fetched at profile load time.
 */
export class SourceMapStore {
  private _consumers: Map<IndexIntoSourceTable, SourceMapConsumer>;

  private constructor(consumers: Map<IndexIntoSourceTable, SourceMapConsumer>) {
    this._consumers = consumers;
  }

  static async create(
    resolvedSourceMaps: Map<IndexIntoSourceTable, RawSourceMap>,
    wasmUrl: string
  ): Promise<SourceMapStore> {
    const consumers = new Map<IndexIntoSourceTable, SourceMapConsumer>();

    // No-op if already initialized.
    LibSourceMapConsumer.initialize({
      'lib/mappings.wasm': wasmUrl,
    });

    await Promise.all(
      Array.from(resolvedSourceMaps).map(async ([sourceIndex, sourceMap]) => {
        try {
          const consumer = await new LibSourceMapConsumer(sourceMap);
          consumers.set(sourceIndex, consumer);
        } catch (e) {
          console.warn(
            `SourceMapStore: failed to parse source map for sourceIndex=${sourceIndex}:`,
            e
          );
        }
      })
    );

    return new SourceMapStore(consumers);
  }

  getConsumer(sourceIndex: IndexIntoSourceTable): SourceMapConsumer | null {
    return this._consumers.get(sourceIndex) ?? null;
  }

  destroy(): void {
    for (const consumer of this._consumers.values()) {
      consumer.destroy();
    }
    this._consumers.clear();
  }
}
